import { FC, useState, useEffect, useCallback, useMemo } from 'react';
import { Popover } from 'antd';
import Image from 'next/image';
import classNames from 'classnames';
import dayjs from 'dayjs';
import Link from 'next/link';
import { getEntryByContentTypeAndEntry } from '@/services/contentstack';
import { getDefaultPromotion, getPromotionDetail } from '@/services/quote';
import { productConfigs, getProductPlanConfig } from '@/constants/productConfig';
import HeaderMenuStyles from './HeaderMegaMenu.module.scss';

export const HeaderMegaMenu: FC<any> = props => {
  const { majorLinks, productMenuData, promotions, discountLabels, calloutBlocks, language } =
    props;
  const [endTime, setEndtime] = useState<any>({});
  const [promotionRef, setPromotionRef] = useState<any>({});
  const [isGreaterThanSevenDays, setIsGreaterThanSevenDays] = useState<boolean>(false);
  const [day, setDay] = useState<number | string>('00');
  const [hour, setHour] = useState('00');
  const [minutes, setMinutes] = useState('00');
  const [seconds, setSeconds] = useState('00');

  const formatPromotionParams = (
    productId: string,
    configurationData?: any,
    promotionData?: any
  ) => {
    /**
     * promotion: get frontendCode as defaultpromocode
     * get default plan option
     */
    const promoCode = (promotionData as any)?.frontendCode;
    const defaultPlanOption = configurationData?.plan_options?.find(
      (item: any) =>
        item?.renewable_period?.is_default === true || item?.deductible?.is_default === true
    );
    const defaultPlan = configurationData?.plans.find((item: any) => item?.is_default === true);
    const deductible = defaultPlanOption?.deductible?.value ?? '';
    const renewablePeriod = defaultPlanOption?.renewable_period?.value ?? '';
    const paymentFrequency = configurationData?.payment_frequency?.default_payment_frequency;
    const curPlanConfig = getProductPlanConfig(productId, defaultPlan.code, {
      sumInsured: configurationData?.sum_insured?.default_value,
      deductible,
      renewablePeriod
    });
    return {
      productId,
      promoCodeTierType: 'Marketing Promo',
      promoCode,
      planCode: curPlanConfig?.planCode,
      deductible,
      optionCode: curPlanConfig?.optionCode,
      paymentFrequency
    };
  };

  const fetchDeaultPromoCode = useCallback(
    async (productId: string) => {
      /**
       *  step 1: get the configuration v2 about the target product
       *          according to productId to get product configuration v2
       *  step 2: get the default promo code
       *
       *  step 3: use the configuration data and default promotion data to format target params for getting promotion detail
       */
      const promotionDataFromCMS = promotions?.length ? promotions?.[0] : {};
      let configV2 = {};
      let defaultPromotionData = {};
      const productConfig = productConfigs.find(item => item.id === productId);
      if (productConfig?.entryId) {
        const configV2IncludeReference: string[] = ['plans', 'plan_options', 'insured_config'];
        await getEntryByContentTypeAndEntry(
          'ecom_product_configuration_v2',
          productConfig.entryId,
          configV2IncludeReference
        ).then((res: any) => {
          configV2 = res.data;
        });
        // 获取defalut promotion data
        const defaultPromotionRes = await getDefaultPromotion(productId);
        defaultPromotionData = defaultPromotionRes?.data;
        const promoParams = formatPromotionParams(productId, configV2, defaultPromotionData);

        const promotionRes = await getPromotionDetail(promoParams);
        const promotionDetail = promotionRes?.data || {};
        setPromotionRef({
          ...promotionDataFromCMS,
          sale_label:
            language.origin === 'zh' ? promotionDetail.shortDescZh : promotionDetail.shortDescEn
        });
        setEndtime(new Date(promotionDetail?.endDate)?.getTime());
      }
    },
    [language.origin, promotions]
  );

  const getInneededProtionData = useCallback(async () => {
    const promotionDataFromCMS = promotions?.length ? promotions?.[0] : {};

    if (promotionDataFromCMS?.sale_label) {
      setPromotionRef(promotionDataFromCMS);
      setEndtime(new Date(promotionDataFromCMS?.end_time)?.getTime());
    } else {
      fetchDeaultPromoCode(promotionDataFromCMS?.product_id);
    }
  }, [fetchDeaultPromoCode, promotions]);

  useEffect(() => {
    // judge the promotion ref in CMS
    getInneededProtionData();
  }, [language.origin]);

  // split
  const splitArray = (array: any[]) => {
    const clone = [...array];
    const newArray = clone.splice(0, Math.ceil(clone.length / 2));
    return [newArray, clone];
  };

  // add GA event
  const handleGA = (title: any, href: any) => {
    window?.dataLayer?.push({
      event: 'menuBar',
      buttonTitle: title,
      destinationUrl: href,
      pageURL: window.location.href,
      productName: title,
      componentName: 'header menu'
    });
  };

  const getTime = () => {
    const formatTime = (time: number) => {
      let timeStr = time.toString();
      if (timeStr.length === 1) {
        return `0${timeStr}`;
      } else {
        return timeStr;
      }
    };
    const targetEndTime = dayjs(endTime);
    const currentTime = dayjs();
    const duration = dayjs.duration(targetEndTime.diff(currentTime));
    const day =
      dayjs(targetEndTime.format('YYYY-MM-DD')).diff(dayjs().format('YYYY-MM-DD'), 'day') || '00';
    const hour = formatTime(duration.hours());
    const minutes = formatTime(duration.minutes());
    const seconds = formatTime(duration.seconds());

    return {
      day,
      hour,
      minutes,
      seconds
    };
  };

  useEffect(() => {
    const currentTime = dayjs();
    const endDate = dayjs(endTime);
    const diffInSeconds = endDate.diff(currentTime, 'seconds');
    setIsGreaterThanSevenDays(diffInSeconds > 7 * 24 * 60 * 60);
    if (!(diffInSeconds > 7 * 24 * 60 * 60)) {
      const timer = setInterval(() => {
        const { day, hour, minutes, seconds } = getTime();
        setDay(day);
        setHour(hour);
        setMinutes(minutes);
        setSeconds(seconds);
      }, 1000);
      return () => {
        clearInterval(timer);
      };
    }
  }, [endTime, language.origin]);

  const CountDown = useMemo(() => {
    if (isGreaterThanSevenDays)
      return language.origin === 'en'
        ? `Offer ends on ${dayjs(endTime).format('DD MMM YYYY')}`
        : `優惠至: ${dayjs(endTime).format('YYYY年MM月DD日')}`;
    if (!isGreaterThanSevenDays) {
      return language.origin === 'en'
        ? `Ends in ${day}d : ${hour}h : ${minutes}m : ${seconds}s`
        : `限時倒數 ${day}日 : ${hour}小時 : ${minutes}分 : ${seconds}秒`;
    }
  }, [day, endTime, hour, isGreaterThanSevenDays, language.origin, minutes, seconds]);

  const productPopContent = (
    <div
      className={classNames(
        HeaderMenuStyles.pop_content,
        language.origin === 'zh' ? HeaderMenuStyles.pop_content_ZH : HeaderMenuStyles.pop_content_EN
      )}
    >
      <div className={HeaderMenuStyles.pop_content_left}>
        {Object.entries(productMenuData)?.map(([key, items], groupIndex) => (
          <div key={Math.random()} className={HeaderMenuStyles.pop_content_left_column}>
            <div
              className={HeaderMenuStyles.pop_content_left_title}
              dangerouslySetInnerHTML={{ __html: key }}
            ></div>
            <div
              className={classNames(
                groupIndex % 2 === 0 ? HeaderMenuStyles.pop_content_left_column_left : ''
              )}
            >
              {(groupIndex % 2 === 0 ? splitArray(items as any) : [items]).map(
                (chunkItems: any, chunkIndex) => (
                  <div key={key + chunkIndex} className={HeaderMenuStyles.chunkItemIndex}>
                    {chunkItems.map((item: any, chunkItemIndex: number) => (
                      <div key={key + chunkIndex + chunkItemIndex}>
                        <div key={Math.random()} className={HeaderMenuStyles.left_item}>
                          <div className={HeaderMenuStyles.left_item_sub}>
                            <a
                              className={HeaderMenuStyles.left_item_href}
                              href={item?.sub_grouping_link?.href}
                            >
                              <div className={HeaderMenuStyles.Icon_Wrap}>
                                {/* {item?.Sub_grouping?.sub_grouping_link.href} */}
                                <Image
                                  width={32}
                                  height={32}
                                  src={item?.icon?.url}
                                  alt={''}
                                  className={HeaderMenuStyles.Icon}
                                />
                              </div>
                              <div
                                className={HeaderMenuStyles.html}
                                dangerouslySetInnerHTML={{
                                  __html: item?.sub_grouping_link?.title ?? ''
                                }}
                              ></div>
                            </a>
                          </div>
                          {item?.items?.length > 0 && (
                            <div className={HeaderMenuStyles.left_item_child_block}>
                              {item?.items.map((childItem: any) => (
                                <a
                                  className={HeaderMenuStyles.left_item_child_href}
                                  key={childItem?._metadata?.uid}
                                  href={childItem?.link_item?.href}
                                >
                                  <div
                                    className={HeaderMenuStyles.left_item_child_title}
                                    dangerouslySetInnerHTML={{
                                      __html: childItem?.link_item?.title ?? ''
                                    }}
                                  />
                                  {childItem?.call_out_label ? (
                                    <span className={HeaderMenuStyles.left_item_call_out_label}>
                                      {childItem?.call_out_label}
                                    </span>
                                  ) : (
                                    ''
                                  )}
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )
              )}
            </div>
          </div>
        ))}
      </div>
      {promotions?.length ? (
        <div className={HeaderMenuStyles.pop_content_right}>
          <div className={HeaderMenuStyles.time_block}>
            <Image
              src="/blog/icon/Clock.svg"
              className={HeaderMenuStyles.Clock}
              alt={''}
              width={24}
              height={24}
            />
            <span className={HeaderMenuStyles.time_text}> {CountDown}</span>
          </div>
          <img src={promotionRef?.image?.url} alt="" className={HeaderMenuStyles.pop_content_img} />
          <div className={HeaderMenuStyles.pop_content_title}>{promotionRef?.title}</div>
          <div className={HeaderMenuStyles.pop_content_discount}>
            <div className={HeaderMenuStyles.pop_content_price}>
              <Image
                src="/blog/icon/discount-no-shadow.svg"
                className={HeaderMenuStyles.Discount}
                alt={''}
                width={40}
                height={38}
              />
              <span>{promotionRef?.sale_label}</span>
            </div>
            <div className={HeaderMenuStyles.pop_content_descr}>{promotionRef?.description}</div>
          </div>

          <div className={HeaderMenuStyles.pop_content_label}>
            <span className={HeaderMenuStyles.pop_content_label_child}>
              <Image
                src="/blog/icon/Tag.svg"
                width={24}
                height={24}
                alt={''}
                className={HeaderMenuStyles.pop_content_label_icon}
              />
              <span>
                <a href={promotionRef?.action_link?.href}>{promotionRef?.action_link?.title}</a>
              </span>
            </span>
          </div>
          {promotionRef?.more_link?.title && (
            <div className={HeaderMenuStyles.more_link}>
              <span className={HeaderMenuStyles.ArrowR}>
                <Image src="/blog/icon/ArrowR.svg" width={20} height={18} alt={''} />
              </span>
              <span>
                <a href={promotionRef?.action_link?.href}>{promotionRef?.more_link?.title}</a>
              </span>
            </div>
          )}

          {calloutBlocks?.[0]?.title_desktop && calloutBlocks?.[0]?.button_label ? (
            <div className={HeaderMenuStyles.Callout_Block}>
              <div className={HeaderMenuStyles.Callout_Block_Title}>
                {calloutBlocks?.[0]?.title_desktop}
              </div>
              <div className={HeaderMenuStyles.Callout_Content}>
                <Link
                  className={HeaderMenuStyles.Content_Left}
                  href={'https:/www.fwd.com.hk' + calloutBlocks?.[0]?.button_url_desktop}
                >
                  <Image src="/blog/icon/ArrowRight.svg" width={20} height={18} alt={''} />
                  <span className={HeaderMenuStyles.Button_Label}>
                    {calloutBlocks?.[0]?.button_label}
                  </span>
                </Link>
                <Image
                  src={calloutBlocks?.[0]?.icon_desktop?.url}
                  width={43}
                  height={43}
                  alt={''}
                />
              </div>
            </div>
          ) : (
            ''
          )}
        </div>
      ) : (
        ''
      )}
    </div>
  );

  const renderDropdownLinks = (dropdownLinks: any) => {
    return (
      <div
        className={classNames(
          HeaderMenuStyles.dropdown,
          dropdownLinks?.some((x: any) => x.siblings?.length) &&
            HeaderMenuStyles.dropdown_with_gap16
        )}
      >
        {dropdownLinks?.map((item: any, index: number) => {
          return (
            <div key={index}>
              <a
                href={item?.href || undefined}
                title={item?.title}
                target={item.isOpenInNewTab ? '_blank' : undefined}
                className={classNames(
                  HeaderMenuStyles.dropdown_item,
                  !!item?.href && HeaderMenuStyles.dropdown_hover_hover_item
                )}
              >
                {item.icon?.url && (
                  <Image
                    src={item.icon?.url}
                    alt={item?.title ?? ''}
                    width={24}
                    height={24}
                    style={{ cursor: 'default' }}
                  />
                )}
                <div
                  className={classNames(
                    dropdownLinks.some((x: any) => x.siblings?.length)
                      ? HeaderMenuStyles.dropdown_item_sibling_title
                      : HeaderMenuStyles.dropdown_item_title
                  )}
                  dangerouslySetInnerHTML={{ __html: item?.title ?? '' }}
                />
              </a>

              {item.siblings?.map((subItem: any, i: any) => {
                return (
                  <div key={index + i} className={HeaderMenuStyles.sibling}>
                    <a
                      href={subItem?.href || undefined}
                      title={subItem?.title}
                      target={subItem.isOpenInNewTab ? '_blank' : undefined}
                      className={classNames(
                        HeaderMenuStyles.sibling_item,
                        !!subItem?.href && HeaderMenuStyles.dropdown_hover_hover_item
                      )}
                    >
                      <div
                        className={classNames(HeaderMenuStyles.sibling_item_title)}
                        dangerouslySetInnerHTML={{ __html: subItem?.title ?? '' }}
                      />
                    </a>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <>
      {majorLinks.map((majorItem: any, index: number) => (
        <span
          key={majorItem?.major_group_link?.title}
          onClick={() => handleGA(majorItem?.major_group_link?.title, '')}
        >
          {majorItem?.dropdownLinks?.length > 0 && (
            <Popover
              content={renderDropdownLinks(majorItem?.dropdownLinks)}
              overlayClassName={classNames(
                HeaderMenuStyles.dropdown_link_popover_layout,
                language.origin === 'en' ? HeaderMenuStyles.dropdown_link_popover_layout_en : ''
              )}
              placement={index === 1 ? 'bottom' : 'bottomLeft'}
              autoAdjustOverflow={false}
              arrow={true}
              align={{
                autoArrow: true,
                offset: index === 1 ? [22, 28] : [-20, 28]
                // points: index === 2 ? ['tc', 'tc'] : ['bc', 'bc']
              }}
            >
              {majorItem?.major_group_link?.title}
            </Popover>
          )}
          {!majorItem?.dropdownLinks?.length && (
            <Popover
              align={{ offset: [0, 28] }}
              content={productPopContent}
              overlayClassName={classNames(
                HeaderMenuStyles.pop_layout,
                language.origin === 'en' ? HeaderMenuStyles.pop_layout_en : ''
              )}
              placement="bottom"
              autoAdjustOverflow={false}
              arrow={{ pointAtCenter: false }}
            >
              {majorItem?.major_group_link?.title}
            </Popover>
          )}
        </span>
      ))}
      <div data-part="divider" />
    </>
  );
};
